import React, { useState, useEffect } from "react";
import {
  FormControl,
  Select,
  MenuItem,
  Stack,
  Box,
  InputAdornment,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import {
  getDateFilterColors,
  getDateFilterPickerSlotProps,
} from "./dateFilterTheme";

const VIEW_OPTIONS = [
  { value: "all", label: "All" },
  { value: "day", label: "Day" },
  { value: "month", label: "Month" },
  { value: "year", label: "Year" },
];

const DateFilterforChart = ({
  label = "Select Date",
  selectLabel = "View",
  value = null,
  onChange,
  onViewChange,
  defaultView = "all",
  availableViews = VIEW_OPTIONS,
  datePickerProps = {},
  selectProps = {},
  minDate,
  maxDate,
  resetKey,
  width,
  height,
  isDashboard = false,
  containerSx = {}
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { border: dateFilterBorder, iconColor: dateFilterIconColor } =
    getDateFilterColors(theme);
  const [selectedDate, setSelectedDate] = useState(value);
  const [viewMode, setViewMode] = useState(defaultView);

  // Reset component when resetKey changes
  useEffect(() => {
    setSelectedDate(null);
    setViewMode(defaultView);

    // Notify parent component about the reset
    if (onChange) {
      onChange(null, defaultView, "");
    }
    if (onViewChange) {
      onViewChange(defaultView);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey, defaultView]);

  // Set up initial values
  useEffect(() => {
    if (value) {
      setSelectedDate(value);
    }
  }, [value]);

  /**
   * Format date based on view mode
   */
  const formatDateByViewMode = (date, mode) => {
    if (!date) return "";

    try {
      // Ensure we're working with a Date object
      const dateObj = new Date(date);

      if (isNaN(dateObj.getTime())) {
        console.error("Invalid date provided:", date);
        return "";
      }

      // Manual formatting without relying on date-fns
      const month = String(dateObj.getMonth() + 1).padStart(2, "0");
      const day = String(dateObj.getDate()).padStart(2, "0");
      const year = dateObj.getFullYear();

      switch (mode) {
        case "day":
          return `${year}-${month}-${day}`;
        case "month":
          return `${year}-${month}`;
        case "year":
          return `${year}`;
        default:
          return "";
      }
    } catch (error) {
      console.error("Date formatting error:", error);
      return "";
    }
  };

  // Handle date change
  const handleDateChange = (newDate) => {
    setSelectedDate(newDate);
    if (onChange) {
      // Format date based on view mode before sending to parent
      const formattedDateStr = formatDateByViewMode(newDate, viewMode);
      onChange(newDate, viewMode, formattedDateStr);
    }
  };

  // Handle view mode change
  const handleViewChange = (event) => {
    const newViewMode = event.target.value;
    setViewMode(newViewMode);

    // Notify parent component about view change
    if (onViewChange) {
      onViewChange(newViewMode);
    }

    // Also call the main onChange with current date and new view mode
    if (onChange && selectedDate) {
      const formattedDateStr = formatDateByViewMode(selectedDate, newViewMode);
      onChange(selectedDate, newViewMode, formattedDateStr);
    } else if (onChange && newViewMode === "all") {
      // If "all" is selected and no date, still notify with empty date
      onChange(null, newViewMode, "");
    }
  };

  /**
   * Determine DatePicker views and openTo based on view mode
   */
 const getDatePickerProps = () => {
  let views, openTo, inputFormat, placeholder;

  switch (viewMode) {
    case "month":
      views = ["year", "month"];
      openTo = "month";
      inputFormat = "MMM yyyy";
      placeholder = "MMM YYYY";
      break;
    case "year":
      views = ["year"];
      openTo = "year";
      inputFormat = "yyyy";
      placeholder = "YYYY";
      break;
    case "day":
      views = ["year", "month", "day"];
      openTo = "day";
      inputFormat = "MM/dd/yyyy";
      placeholder = "MM/DD/YYYY";
      break;
    default:
      views = ["year", "month", "day"];
      openTo = "day";
      inputFormat = "MM/dd/yyyy";
      placeholder = "Select Date";
  }

  return {
    value: selectedDate,
    onChange: handleDateChange,
    views,
    openTo,
    inputFormat,
    closeOnSelect: true,
    slotProps: getDateFilterPickerSlotProps(theme, {
      textField: {
        size: "small",
        inputProps: {
          placeholder,
        },
        sx: {
          "& .MuiInputBase-root": {
            height: isDashboard ? "30px" : "42px",
            borderRadius: isDashboard ? "4px" : "6px",
            fontSize: isDashboard ? "0.75rem" : "0.875rem",
            background: theme.palette.background.default,
          },
        },
      },
    }),
    ...datePickerProps,
  };
};

  // For small screens, we want a simplified layout
  if (isMobile) {
    return (
      <Stack 
        spacing={1} 
        sx={{ 
          width: '100%',
          ...containerSx
        }}
      >
        <FormControl 
          sx={{ 
            width: '100%',
            maxWidth: '100%',
          }} 
          {...selectProps} 
          size="small"
        >
          <span id="date-filter-select-label-mobile" style={{ display: 'none' }}>
            {selectLabel}
          </span>
          <Select
            labelId="date-filter-select-label-mobile"
            id="date-filter-select-mobile"
            value={viewMode}
            aria-labelledby="date-filter-select-label-mobile"
            onChange={handleViewChange}
            size="small"
            sx={{
              border: `1px solid ${dateFilterBorder}`,
              height: "42px",
              borderRadius: "6px",
              "& .MuiOutlinedInput-notchedOutline": {
                border: "none",
              },
            }}
            startAdornment={
              <InputAdornment position="start">
                <CalendarMonthIcon sx={{ color: dateFilterIconColor }} />
              </InputAdornment>
            }
          >
            {availableViews.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        
        {viewMode !== "all" && (
          <Box sx={{ width: '100%' }}>
            <DatePicker {...getDatePickerProps()} />
          </Box>
        )}
      </Stack>
    );
  }

  // For larger screens, use the horizontal layout
  return (
    <Stack
      spacing={2}
      direction="row"
      alignItems="center"
      sx={{
        width: width || "auto",
        height,
        ...containerSx,
      }}
    >
      <FormControl
        sx={{
          minWidth: isDashboard ? 100 : 120,
          width: isDashboard ? 120 : "auto",
        }}
        {...selectProps}
        size="small"
      >
        <span id="date-filter-select-label-desktop" style={{ display: 'none' }}>
          {selectLabel}
        </span>
        <Select
          labelId="date-filter-select-label-desktop"
          id="date-filter-select-desktop"
          value={viewMode}
          aria-labelledby="date-filter-select-label-desktop"
          onChange={handleViewChange}
          size="small"
          variant="outlined"
          sx={{
            border: `1px solid ${dateFilterBorder}`,
            height: isDashboard ? 30 : 42,
            minHeight: isDashboard ? 30 : 42,
            borderRadius: isDashboard ? "4px" : "6px",
            fontSize: isDashboard ? "0.75rem" : "0.875rem",
            paddingLeft: isDashboard ? 0.5 : 1,
            "& .MuiOutlinedInput-notchedOutline": {
              border: "none",
            },
          }}
          startAdornment={
            <InputAdornment position="start">
              <CalendarMonthIcon
                sx={{ color: dateFilterIconColor }}
                fontSize={isDashboard ? "small" : "medium"}
              />
            </InputAdornment>
          }
        >
          {availableViews.map((option) => (
            <MenuItem
              key={option.value}
              value={option.value}
              sx={{
                fontSize: isDashboard ? "0.75rem" : "0.875rem",
                minHeight: isDashboard ? 30 : 42,
              }}
            >
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {viewMode !== "all" && (
        <Box sx={{ width: "auto", minWidth: "200px" }}>
          <DatePicker {...getDatePickerProps()} />
        </Box>
      )}
    </Stack>
  );
};

export default DateFilterforChart;